import React from "react";
import { Icon } from "../../";
import {
  getBusinessPOSIntegrations,
  getBusinessSalesChannelIntegrations,
} from "../../../../hooks";

const SalesChannelBusinessCard = ({ business, integration, onSelect }) => {
  const { name, logo, third_party_integrations = [] } = business;

  const businessPOSIntegrations = getBusinessPOSIntegrations(business);
  const businessSalesChannelIntegrations =
    getBusinessSalesChannelIntegrations(business);

  const getIntegrationStatus = (integrationId) => {
    const businessIntegration = third_party_integrations.find(
      (tpi) => tpi.third_party_integration_id === integrationId,
    );

    if (!businessIntegration) return null;

    const isConnected =
      businessIntegration.setup_status === "success" ||
      (businessIntegration.enabled &&
        businessIntegration.setup_status !== null);

    return isConnected ? "connected" : "pending";
  };

  const handleCardClick = (e) => {
    if (e.target.closest("button") || e.target.closest('[role="button"]')) {
      return;
    }
    onSelect && onSelect(business);
  };

  return (
    <div
      onClick={handleCardClick}
      className="group h-full w-full cursor-pointer rounded-lg border border-gray-800 bg-gray-900 p-4 text-left transition-colors hover:border-gray-700 hover:bg-gray-800"
    >
      {/* Business Info */}
      <div className="flex min-w-0 items-start gap-3">
        {logo ? (
          <img
            src={logo}
            alt={name}
            className="h-10 w-10 flex-shrink-0 rounded-lg object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gray-800">
            <Icon name="Store" className="h-6 w-6 text-gray-400" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center justify-between gap-2">
            <div className="font-small min-w-0 flex-1 truncate text-white">
              {name}
            </div>
            <div className="flex flex-shrink-0 items-center gap-1 text-xs text-gray-400">
              <span>Connect</span>
              <Icon name="ChevronRight" className="h-4 w-4" />
            </div>
          </div>

          {/* POS Integrations Section */}
          {businessPOSIntegrations.length > 0 && (
            <div className="mt-2 border-t border-gray-700 pt-2">
              <div className="mb-2 text-xs text-gray-400">
                POS Integrations:
              </div>
              <div className="space-y-1.5">
                {businessPOSIntegrations.map((posIntegration) => {
                  const status = getIntegrationStatus(posIntegration.id);

                  return (
                    <div
                      key={posIntegration.id}
                      className="flex items-center justify-between gap-2"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        {posIntegration.logo ? (
                          <img
                            src={posIntegration.logo}
                            alt={posIntegration.name}
                            className="h-5 w-5 flex-shrink-0 rounded object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-gray-700">
                            <Icon
                              name="Package"
                              className="h-3 w-3 text-gray-400"
                            />
                          </div>
                        )}
                        <span className="truncate text-xs text-gray-300">
                          {posIntegration.name}
                        </span>
                      </div>
                      {status && (
                        <span
                          className={`inline-flex flex-shrink-0 items-center rounded-full px-1.5 py-0.5 text-xs font-medium ${
                            status === "connected"
                              ? "border border-green-500/30 bg-green-900/20 text-green-400"
                              : "border border-yellow-500/30 bg-yellow-900/20 text-yellow-400"
                          }`}
                        >
                          {status === "connected" ? "Connected" : "Pending"}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sales Channel Integrations Section */}
          {businessSalesChannelIntegrations.length > 0 && (
            <div className="mt-2 border-t border-gray-700 pt-2">
              <div className="mb-2 text-xs text-gray-400">Sales Channels:</div>
              <div className="space-y-1.5">
                {businessSalesChannelIntegrations.map((scIntegration) => {
                  const status = getIntegrationStatus(scIntegration.id);
                  const isSelected = scIntegration.id === integration?.id;

                  return (
                    <div
                      key={scIntegration.id}
                      className="flex items-center justify-between gap-2"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        {scIntegration.logo ? (
                          <img
                            src={scIntegration.logo}
                            alt={scIntegration.name}
                            className="h-5 w-5 flex-shrink-0 rounded object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-gray-700">
                            <Icon
                              name="ShoppingCart"
                              className="h-3 w-3 text-gray-400"
                            />
                          </div>
                        )}
                        <span
                          className={`truncate text-xs ${isSelected ? "font-medium text-white" : "text-gray-300"}`}
                        >
                          {scIntegration.name}
                        </span>
                      </div>
                      {status && (
                        <span
                          className={`inline-flex flex-shrink-0 items-center rounded-full px-1.5 py-0.5 text-xs font-medium ${
                            status === "connected"
                              ? "border border-green-500/30 bg-green-900/20 text-green-400"
                              : "border border-yellow-500/30 bg-yellow-900/20 text-yellow-400"
                          }`}
                        >
                          {status === "connected" ? "Connected" : "Pending"}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesChannelBusinessCard;
